import clsx from 'clsx'
import { type PropsWithChildren, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowDownIcon } from '@/assets/icons/arrow-down-icon'
import { LogOutIcon } from '@/assets/icons/log-out-icon'
import { UserIcon } from '@/assets/icons/user-icon'
import { Button } from '@/components/ui/button'
import { ROUTES } from '@/constants/routes'
import { useAuthStore } from '@/store/auth-store'
import { useStudentSessionStore } from '@/store/student-session-store'
import { useCurrentRoute } from './hooks/use-current-route'

interface UserSessionProps extends PropsWithChildren {
  name: string | null
  signOut: () => void
}

const UserSession = ({ name, children, signOut }: UserSessionProps) => {
  const [active, setActive] = useState<boolean>(false)

  const toggle = () => {
    setActive(!active)
  }

  return (
    <div className="relative min-w-60 z-50">
      <button
        type="button"
        onClick={toggle}
        className={clsx(
          'cursor-pointer flex text-background px-3 items-center justify-center h-10 gap-3 font-bold w-full border-b',
          {
            'bg-[var(--brand-blue-2)] border-background': active
          },
          {
            'bg-transparent border-transparent': !active
          }
        )}
      >
        <UserIcon />
        <p>{name}</p>
        <ArrowDownIcon />
      </button>
      <div
        className={clsx(
          'absolute w-full bg-[var(--brand-blue-2)] px-3 rounded-b-md overflow-hidden',
          {
            block: active
          },
          {
            hidden: !active
          }
        )}
      >
        <ul>
          {children}
          <Button
            onClick={signOut}
            className="text-base flex items-center text-background"
          >
            <p>Sair do sistema</p>
            <LogOutIcon />
          </Button>
        </ul>
      </div>
    </div>
  )
}

interface SessionButtonProps extends PropsWithChildren {
  currentRoute: string
  redirectTo: string
}

const SessionButton = ({
  children,
  currentRoute,
  redirectTo
}: SessionButtonProps) => {
  const navigate = useNavigate()
  return (
    <Button
      onClick={() => {
        navigate(`/${redirectTo}`)
      }}
      variant={'textOnly'}
      disabled={redirectTo === currentRoute}
      className="text-base flex items-center text-background"
    >
      {children}
    </Button>
  )
}

interface ManagerSessionProps {
  name: string | null
}

interface StudentSessionProps {
  name: string | null
}

export const ManagerSession = ({ name }: ManagerSessionProps) => {
  const route = useCurrentRoute()
  const { clearSession } = useAuthStore()
  const navigate = useNavigate()

  const signOut = () => {
    clearSession()
    navigate(`/${ROUTES.SIGN_IN.route}`)
  }

  return (
    <UserSession signOut={signOut} name={name}>
      <li>
        <SessionButton
          currentRoute={route}
          redirectTo={ROUTES.MANAGER_PANEL.route}
        >
          Menu das Solicitações
        </SessionButton>
      </li>
      <li>
        <SessionButton
          currentRoute={route}
          redirectTo={ROUTES.MY_REVIEWS.route}
        >
          Minhas Análises
        </SessionButton>
      </li>
    </UserSession>
  )
}

export const StudentSession = ({ name }: StudentSessionProps) => {
  const route = useCurrentRoute()
  const { clearSession } = useStudentSessionStore()
  const navigate = useNavigate()

  const signOut = () => {
    clearSession()
    navigate(`/${ROUTES.EXPIRED_TOKEN.route}`)
  }

  return (
    <UserSession signOut={signOut} name={name}>
      <li>
        <SessionButton
          currentRoute={route}
          redirectTo={ROUTES.STUDENT_HOME.route}
        >
          Página Inicial
        </SessionButton>
      </li>
    </UserSession>
  )
}
